import { useNavigate } from "react-router-dom";
import {
  FolderIcon,
  UserIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  BeakerIcon,
} from "@heroicons/react/24/solid";
import { DOCS_URL, CHANGELOG_URL } from "@utils/constant";
import { ROUTE_DASHBOARD } from "@constants/routes";
import Branding from "./Branding";

export type SidebarItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  type?: "item" | "spacer" | "divider";
};

interface SidebarProps {
  activeItem?: string;
  items?: SidebarItem[];
}

const defaultItems: SidebarItem[] = [
  {
    id: "templates",
    label: "Templates",
    icon: <FolderIcon className="h-5 w-5" />,
    href: ROUTE_DASHBOARD,
    type: "item",
  },
  {
    id: "spacer1",
    label: "",
    icon: null,
    type: "spacer",
  },
  {
    id: "divider2",
    label: "",
    icon: null,
    type: "divider",
  },
  {
    id: "playground",
    label: "Playground",
    icon: <BeakerIcon className="h-5 w-5" />,
    href: "/playground",
    type: "item",
  },
  {
    id: "api-docs",
    label: "API Docs",
    icon: <DocumentTextIcon className="h-5 w-5" />,
    href: DOCS_URL,
    type: "item",
  },
  {
    id: "changelog",
    label: "Changelog",
    icon: <ClockIcon className="h-5 w-5" />,
    href: CHANGELOG_URL,
    type: "item",
  },
  {
    id: "divider1",
    label: "",
    icon: null,
    type: "divider",
  },
  {
    id: "profile",
    label: "Profile",
    icon: <UserIcon className="h-5 w-5" />,
    href: "/profile",
    type: "item",
  },
];

function SidebarNavItem({
  item,
  active = false,
  onClick,
}: {
  item: SidebarItem;
  active?: boolean;
  onClick?: () => void;
}) {
  if (item.type === "spacer") {
    return <div className="flex-1" />;
  }

  if (item.type === "divider") {
    return <div className="my-2 h-px bg-gray-200" />;
  }

  return (
    <button
      className={`flex items-center space-x-3 rounded-lg px-3 py-2 text-base font-medium transition hover:bg-gray-100 ${
        active ? "bg-gray-200 text-gray-900" : "text-gray-700"
      }`}
      onClick={onClick}
    >
      {item.icon}
      <span>{item.label}</span>
      {item.href && item.href.startsWith("http") && (
        <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
      )}
    </button>
  );
}

function Sidebar({ activeItem, items = defaultItems }: SidebarProps) {
  const navigate = useNavigate();

  return (
    <aside className="flex h-full w-56 flex-col border-r bg-gray-50 px-4 py-6 shadow-sm">
      {/* Logo */}
      <div className="mb-8 px-2">
        <Branding />
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col space-y-2">
        {items.map((item) => (
          <SidebarNavItem
            key={item.id}
            item={item}
            active={activeItem === item.id}
            onClick={() => {
              if (item.href && item.type === "item") {
                if (item.href.startsWith("http")) {
                  window.open(item.href, "_blank");
                } else {
                  navigate(item.href);
                }
              }
            }}
          />
        ))}
      </nav>
    </aside>
  );
}

export default Sidebar;
